import React from 'react';
import { PlusCircle, Trash2, Film } from 'lucide-react';
import { Job } from '../api/client';

interface JobsDashboardProps {
  jobs: Job[];
  navigateTo: (tab: 'jobs' | 'new_job' | 'review' | 'settings', jobId?: string | null) => void;
  handleDeleteJob: (id: string, title: string) => void;
  handleOpenReview: (id: string) => void;
}

export const JobsDashboard: React.FC<JobsDashboardProps> = ({
  jobs,
  navigateTo,
  handleDeleteJob,
  handleOpenReview,
}) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">รายการสื่อทั้งหมด</h1>
          <p className="text-sm text-slate-400">ตรวจสอบสถานะการผลิตวิดีโอ การอนุมัติบท และการเรนเดอร์</p>
        </div>
        <button
          onClick={() => navigateTo('new_job')}
          className="px-4 py-2 bg-sky-500 hover:bg-sky-600 rounded-lg text-sm font-medium text-white flex items-center gap-2 transition shadow-lg shadow-sky-500/20"
        >
          <PlusCircle className="w-4 h-4" /> สร้างงานใหม่
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {jobs.map((job) => (
          <div
            key={job.id}
            className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-5 hover:border-sky-500/40 transition flex flex-col justify-between"
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-sky-500/10 text-sky-400 border border-sky-500/20 font-medium">
                  TikTok 9:16
                </span>
                <span className="text-xs text-slate-400">Rev #{job.commandRevision}</span>
              </div>
              <h3 className="font-semibold text-lg text-white line-clamp-1">{job.title}</h3>
              <p className="text-xs text-slate-400">
                สร้างเมื่อ: {new Date(job.createdAt).toLocaleString('th-TH')}
              </p>

              {(() => {
                const provs = job.providers || (job as any)?.spec?.spec?.providers || (job as any)?.spec?.providers;
                if (!provs) return null;
                const hasAny = Boolean(provs.script || provs.image || provs.audio);
                if (!hasAny) return null;

                return (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {provs.script && (
                      <span className="text-[10px] px-2 py-0.5 rounded bg-sky-950/60 text-sky-300 border border-sky-800/40">
                        บท: {provs.script.name || provs.script.model || provs.script.provider}
                      </span>
                    )}
                    {provs.image && (
                      <span className="text-[10px] px-2 py-0.5 rounded bg-purple-950/60 text-purple-300 border border-purple-800/40">
                        ภาพ: {provs.image.name || provs.image.model || provs.image.provider}
                      </span>
                    )}
                    {provs.audio && (
                      <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-950/60 text-emerald-300 border border-emerald-800/40">
                        เสียง: {provs.audio.name || provs.audio.voice || provs.audio.provider}
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>

            <div className="mt-6 pt-4 border-t border-slate-700/60 flex items-center justify-between">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteJob(job.id, job.title);
                }}
                className="px-2.5 py-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition border border-transparent hover:border-rose-500/20 text-xs flex items-center gap-1.5"
                title="ลบงานนี้"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>ลบงาน</span>
              </button>
              <button
                onClick={() => handleOpenReview(job.id)}
                className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs font-medium text-white transition flex items-center gap-1.5"
              >
                เปิดตรวจฉาก &rarr;
              </button>
            </div>
          </div>
        ))}

        {jobs.length === 0 && (
          <div className="col-span-full text-center py-16 border border-dashed border-slate-800 rounded-2xl bg-slate-900/40">
            <Film className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">ยังไม่มีงานในระบบ</p>
            <button
              onClick={() => navigateTo('new_job')}
              className="mt-4 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm text-sky-400 border border-slate-700 transition"
            >
              กดสร้างงานแรกที่นี่
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
